var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import React, { useState, forwardRef, useCallback } from 'react';
import { useInView } from 'react-intersection-observer';
import { encode } from 'universal-base64';
var isSsr = typeof window === 'undefined';
var isIntersectionObserverAvailable = isSsr
    ? false
    : !!window.IntersectionObserver;
var imageAddStrategy = function (_a) {
    var lazyLoad = _a.lazyLoad, inView = _a.inView, loaded = _a.loaded;
    if (!lazyLoad) {
        return true;
    }
    if (isSsr) {
        return false;
    }
    if (isIntersectionObserverAvailable) {
        return inView || loaded;
    }
    return true;
};
var imageShowStrategy = function (_a) {
    var lazyLoad = _a.lazyLoad, loaded = _a.loaded;
    if (!lazyLoad) {
        return true;
    }
    if (isSsr) {
        return false;
    }
    if (isIntersectionObserverAvailable) {
        return loaded;
    }
    return true;
};
export var Image = forwardRef(function (_a, ref) {
    var _b, _c, _d, _e, _f, _g, _h, _j;
    var className = _a.className, _k = _a.fadeInDuration, fadeInDuration = _k === void 0 ? 500 : _k, intersectionTreshold = _a.intersectionTreshold, intersectionThreshold = _a.intersectionThreshold, intersectionMargin = _a.intersectionMargin, pictureClassName = _a.pictureClassName, _l = _a.lazyLoad, lazyLoad = _l === void 0 ? true : _l, style = _a.style, pictureStyle = _a.pictureStyle, _m = _a.layout, layout = _m === void 0 ? 'intrinsic' : _m, objectFit = _a.objectFit, objectPosition = _a.objectPosition, data = _a.data, onLoad = _a.onLoad, _o = _a.usePlaceholder, usePlaceholder = _o === void 0 ? true : _o;
    var _p = useState(false), loaded = _p[0], setLoaded = _p[1];
    var handleLoad = function () {
        onLoad === null || onLoad === void 0 ? void 0 : onLoad();
        setLoaded(true);
    };
    var _q = useInView({
        threshold: intersectionThreshold || intersectionTreshold || 0,
        rootMargin: intersectionMargin || '0px 0px 0px 0px',
        triggerOnce: true,
        fallbackInView: true
    }), viewRef = _q[0], inView = _q[1];
    var callbackRef = useCallback(function (_ref) {
        viewRef(_ref);
        if (ref)
            ref.current = _ref;
    }, [viewRef]);
    var absolutePositioning = {
        position: 'absolute',
        left: 0,
        top: 0,
        width: '100%',
        height: '100%'
    };
    var addImage = imageAddStrategy({
        lazyLoad: lazyLoad,
        inView: inView,
        loaded: loaded
    });
    var showImage = imageShowStrategy({
        lazyLoad: lazyLoad,
        inView: inView,
        loaded: loaded
    });
    var webpSource = data.webpSrcSet && (React.createElement("source", { srcSet: data.webpSrcSet, sizes: (_b = data.sizes) !== null && _b !== void 0 ? _b : undefined, type: "image/webp" }));
    var regularSource = data.srcSet && (React.createElement("source", { srcSet: data.srcSet, sizes: (_c = data.sizes) !== null && _c !== void 0 ? _c : undefined }));
    var transition = fadeInDuration > 0 ? "opacity ".concat(fadeInDuration, "ms") : undefined;
    var placeholder = usePlaceholder ? (React.createElement("img", { role: "presentation", "aria-hidden": "true", alt: "", src: (_d = data.base64) !== null && _d !== void 0 ? _d : undefined, style: {
            backgroundColor: (_e = data.bgColor) !== null && _e !== void 0 ? _e : undefined,
            opacity: showImage ? 0 : 1,
            transition: transition,
            objectFit: objectFit,
            objectPosition: objectPosition,
            position: 'absolute',
            left: 0,
            top: 0,
            width: '100%'
        } })) : null;
    var width = data.width, aspectRatio = data.aspectRatio;
    var height = data.height || width / aspectRatio;
    var svg = "<svg xmlns=\"http://www.w3.org/2000/svg\" width=\"".concat(width, "\" height=\"").concat(height, "\"></svg>");
    var sizer = layout !== 'fill' ? (React.createElement("img", { className: pictureClassName, style: {
            display: 'block',
            width: '100%'
        }, src: "data:image/svg+xml;base64,".concat(encode(svg)), "aria-hidden": "true", alt: "" })) : null;
    return (React.createElement("div", { ref: callbackRef, className: className, style: __assign(__assign({ overflow: 'hidden' }, (layout === 'fill'
            ? absolutePositioning
            : layout === 'intrinsic'
                ? { position: 'relative', width: '100%', maxWidth: width }
                : layout === 'fixed'
                    ? { position: 'relative', width: width }
                    : { position: 'relative', width: '100%' })), style) },
        sizer,
        placeholder,
        addImage && (React.createElement("picture", null,
            webpSource,
            regularSource,
            data.src && (React.createElement("img", { src: data.src, alt: (_f = data.alt) !== null && _f !== void 0 ? _f : '', title: (_g = data.title) !== null && _g !== void 0 ? _g : undefined, onLoad: handleLoad, className: pictureClassName, style: __assign(__assign(__assign({ opacity: showImage ? 1 : 0, transition: transition }, absolutePositioning), { objectFit: objectFit, objectPosition: objectPosition }), pictureStyle) })))),
        React.createElement("noscript", null,
            React.createElement("picture", null,
                webpSource,
                regularSource,
                data.src && (React.createElement("img", { src: data.src, alt: (_h = data.alt) !== null && _h !== void 0 ? _h : '', title: (_j = data.title) !== null && _j !== void 0 ? _j : undefined, className: pictureClassName, style: __assign(__assign({}, absolutePositioning), pictureStyle), loading: "lazy" }))))));
});
//# sourceMappingURL=index.js.map